"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, CircleCheck, Calendar, Mail, Phone, MessageSquare, UserRound } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "@/components/common/Link";
export default function MedicalConsultationRequest() {
  return <section data-section-id="56140" id="request-your-consultation" className="bg-background text-foreground relative overflow-hidden py-12 md:py-20">
      <div className="pointer-events-none absolute inset-0 flex justify-center overflow-hidden">
        <div className="bg-primary/20 absolute top-[10%] left-[50%] h-[500px] w-[500px] -translate-x-1/2 rounded-full blur-3xl opacity-50" />
        <div className="bg-accent/20 absolute bottom-[10%] right-[10%] h-[300px] w-[300px] rounded-full blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <Card className="border-border/50 shadow-xl backdrop-blur-sm bg-card">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-default">Book a Consultation</CardTitle>
              <CardDescription className="font-default">Our patient support team will review your case and get back to you within one working day.</CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider formKey="request_consultation_home" formId="request_consultation_home" sectionName="request-your-consultation" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              preferred_date: "",
              nature_of_pain: "",
              message: "",
              consent: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              phone_number: {
                required: true,
                pattern: "phone"
              },
              preferred_date: {
                required: false
              },
              nature_of_pain: {
                required: false
              },
              message: {
                required: true
              },
              consent: {
                required: "You must consent to continue"
              }
            }} submitText="Submit Request" successMessage="Your consultation request has been received. Our Irish support team will contact you shortly.">
                <ConsultationForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-accent/20 text-accent-foreground mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-foreground mb-2 text-xl font-semibold font-default">Request Sent Successfully</h3>
        <p className="text-muted-foreground mb-6 max-w-md font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="min-w-[150px] rounded-full font-default">Submit Another Request</Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
            <FormLabel className="font-default">Full Name</FormLabel>
            <FormControl>
              <div className="relative">
                <UserRound className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                <Input placeholder="John Doe" className="!pl-11 font-default rounded-full" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
            <FormLabel className="font-default">Email Address</FormLabel>
            <FormControl>
              <div className="relative">
                <Mail className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                <Input type="email" placeholder="john@example.com" className="!pl-11 font-default rounded-full" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
            <FormLabel className="font-default">Phone Number</FormLabel>
            <FormControl>
              <div className="relative">
                <Phone className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                <Input type="tel" placeholder="+353 87 123 4567" className="!pl-11 font-default rounded-full" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
            <FormLabel className="font-default">Preferred Consultation Date</FormLabel>
            <FormControl>
              <div className="relative">
                <SafeHydrate><Calendar className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" /></SafeHydrate>
                <Input type="text" placeholder="dd/mm/yyyy" className="!pl-11 font-default rounded-full" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="nature_of_pain" render={({
        field
      }) => <FormItem className="col-span-2">
            <FormLabel className="font-default">Nature of Pain</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="font-default rounded-full">
                  <SelectValue placeholder="Select nature of pain" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Chronic Back Pain">Chronic Back Pain</SelectItem>
                <SelectItem value="Sciatica & Leg Pain">Sciatica & Leg Pain</SelectItem>
                <SelectItem value="Neck & Arm Pain">Neck & Arm Pain</SelectItem>
                <SelectItem value="Spinal Stenosis">Spinal Stenosis</SelectItem>
                <SelectItem value="Post-Surgical Pain">Post-Surgical Pain</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-xs font-default">Select the primary area requiring specialist attention.</FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Please provide a description"
      }} render={({
        field
      }) => <FormItem className="col-span-2">
            <FormLabel className="font-default">Description of Your Condition</FormLabel>
            <FormControl>
              <div className="relative">
                <MessageSquare className="text-muted-foreground pointer-events-none absolute top-3 left-3 h-4 w-4" />
                <Textarea placeholder="Tell us about your symptoms, how long you've had them, and any previous treatments in Ireland." className="min-h-[120px] !pl-11 resize-y font-default rounded-xl" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="consent" rules={{
      required: "You must consent to continue"
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="text-sm font-normal leading-snug font-default cursor-pointer text-muted-foreground">
                I consent to Clinic Agatas processing my personal and health data to respond to my enquiry, as described in the{" "}
                <Link to="#" className="text-accent-foreground underline hover:opacity-80">
                  Privacy Policy
                </Link>
                . I can withdraw consent at any time.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="bg-destructive/10 text-destructive border-destructive/20 flex items-center gap-2 rounded-md border p-3 text-sm font-default">
          <span className="font-semibold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-default font-semibold rounded-full shadow-md transition-all hover:shadow-lg" size="lg">
        {isSubmitting ? <><Loader className="mr-2 h-4 w-4 animate-spin" />Submitting Request...</> : 'Book a consultation'}
      </Button>
    </Form>;
}